import { useEffect, useRef, useState } from "react";

export interface TimerProps {
  /** Startwert in Sekunden */
  initialSeconds?: number;
  /** Läuft der Timer gerade? */
  running: boolean;
  /** Wird jede Sekunde aufgerufen mit dem aktuellen Wert */
  onTick?: (seconds: number) => void;
  className?: string;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function Timer({ initialSeconds = 0, running, onTick, className = "" }: TimerProps) {
  const [seconds, setSeconds] = useState(initialSeconds);
  const secondsRef = useRef(initialSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        secondsRef.current += 1;
        const next = secondsRef.current;
        setSeconds(next);
        // Call onTick outside the state updater to avoid "setState during render" warnings
        onTickRef.current?.(next);
      }, 1000);
    } else {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [running]);

  // Wenn initialSeconds von außen geändert wird (z.B. Reset)
  useEffect(() => {
    secondsRef.current = initialSeconds;
    setSeconds(initialSeconds);
  }, [initialSeconds]);

  return (
    <div className={`font-mono text-lg tabular-nums ${className}`} aria-label={`Zeit: ${formatTime(seconds)}`}>
      {formatTime(seconds)}
    </div>
  );
}
